import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    Tooltip,
    YAxis,
    Cell,
} from 'recharts';
import PositiveNegativeBarGraphTooltip from './PositiveNegativeBarGraphTooltip';
import { colors } from '../../../../shared/constants/constants';
import SelectorContainer from '../../SelectorContainer';

// eslint-disable-next-line complexity
function PositiveNegativeBarGraph(props) {
    const {
        barChartProperties,
        barProperties,
        colorPalette = colors,
        positiveColor = colorPalette[0],
        negativeColor = colorPalette[0],
        responsiveContainerProperties,
        suffix,
        timeFormat,
        tooltipLabel,
        tooltipProperties,
    } = props.chartProperties;

    function renderCell(point) {
        const color =
            point.hidden === props.threshold.value
                ? positiveColor
                : negativeColor;

        return <Cell key={props.dataLabel} fill={color} />;
    }

    return (
        <SelectorContainer
            isActive={props.isActive}
            isSelectable={props.isSelectable}
            onSelectorClick={props.onSelectorClick}
            onSelectorRemoveClick={props.onSelectorRemoveClick}
            title={props.title}
            titleWidth={props.titleWidth}
        >
            <div className="positive-negative-bar-graph__chart">
                <ResponsiveContainer {...responsiveContainerProperties}>
                    <BarChart {...barChartProperties} data={props.dataPoints}>
                        <XAxis hide={true} />
                        <YAxis
                            hide={true}
                            domain={props.domain}
                            allowDataOverflow={true}
                        />
                        <Tooltip
                            {...tooltipProperties}
                            cursor={
                                props.dataPoints.length !== 0
                            }
                            content={
                                <PositiveNegativeBarGraphTooltip
                                    axisFormatter={props.dateFormatter}
                                    axisKey={props.dataLabel}
                                    dataFormat={timeFormat}
                                    dataFormatter={props.dataFormatter}
                                    metricValueKey={props.metricValueKey}
                                    suffix={suffix}
                                    thresholdValue={props.threshold.value}
                                    tooltipLabel={tooltipLabel}
                                />
                            }
                        />
                        <Bar
                            {...barProperties}
                            key="hidden"
                            dataKey="hidden"
                            fill="transparent"
                            stackId="default"
                        />
                        <Bar
                            {...barProperties}
                            key={props.dataLabel}
                            dataKey={props.dataLabel}
                            stackId="default"
                        >
                            {props.dataPoints.map(renderCell)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </SelectorContainer>
    );
}

PositiveNegativeBarGraph.propTypes = {
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataFormatter: PropTypes.func,
    dataLabel: PropTypes.string.isRequired,
    dataPoints: PropTypes.instanceOf(Array),
    dateFormatter: PropTypes.func.isRequired,
    domain: PropTypes.instanceOf(Array),
    isActive: PropTypes.bool.isRequired,
    isSelectable: PropTypes.bool.isRequired,
    metricValueKey: PropTypes.string.isRequired,
    onSelectorClick: PropTypes.func.isRequired,
    onSelectorRemoveClick: PropTypes.func.isRequired,
    threshold: PropTypes.instanceOf(Object).isRequired,
    title: PropTypes.string.isRequired,
    titleWidth: PropTypes.number,
};

PositiveNegativeBarGraph.defaultProps = {
    dataPoints: [],
};

/* eslint-disable complexity */
export default React.memo(
    PositiveNegativeBarGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds) &&
        _.isEqual(prevProps.isActive, nextProps.isActive) &&
        _.isEqual(prevProps.titleWidth, nextProps.titleWidth)
);
